import { SetStateAction, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import {
  getSignupFormValidationSchema,
  signupFormDefaultValue,
  signupFormType,
} from './utils';
import { Captcha } from 'features/captcha';
import { useSignup } from 'features/auth/hooks/use-auth';
import { useNavigate } from 'react-router-dom';
import { FormCheckbox } from 'components/core/uCheckbox/formCheckbox';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'hooks/use-config';

/**
 * SignupForm Component
 *
 * A user registration form component that collects username (email) and verifies user interaction via CAPTCHA
 * before allowing form submission. It ensures basic validation using a Zod schema and integrates reCAPTCHA for
 * bot protection.
 *
 * Features:
 * - Username (email) field with validation
 * - Form validation using Zod and React Hook Form
 * - Google reCAPTCHA integration for bot prevention
 * - CAPTCHA required for enabling form submission
 * - Terms of Service and Privacy Policy acknowledgement checkbox
 * - Submit button disabled until CAPTCHA is verified
 * - Loading state handling during async submission
 *
 * @returns {JSX.Element} The rendered signup form with validation and CAPTCHA security
 *
 * @example
 * // Basic usage
 * <SignupForm />
 *
 * // Within a registration page
 * <div className="auth-container">
 *   <h1>Create Your Account</h1>
 *   <SignupForm />
 *   <div className="auth-footer">
 *     <p>Already have an account? <Link to="/signin">Sign in</Link></p>
 *   </div>
 * </div>
 */

export const SignupForm = () => {
  const { t } = useTranslation();
  const [captchaToken, setCaptchaToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { configurationSettings } = useConfig();
  const navigate = useNavigate();
  const { isPending, mutateAsync } = useSignup();
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const [resetCaptcha, setResetCaptcha] = useState<() => void>(() => {});

  const form = useForm<signupFormType>({
    defaultValues: signupFormDefaultValue,
    resolver: zodResolver(getSignupFormValidationSchema),
  });

  const { isValid } = form.formState;

  const handleCaptchaVerify = (token: SetStateAction<string>) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken('');
  };

  const onSubmitHandler = async (values: signupFormType) => {
    if (!captchaToken) {
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await mutateAsync({
        captchaCode: captchaToken,
        ...values,
      });

      if (res.isSuccess) {
        navigate('/signup-email-sent', {
          state: { email: values.email },
        });
      }
    } catch (_error) {
      console.error(_error);
    } finally {
      setCaptchaToken('');
      resetCaptcha();
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmitHandler)}>
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-high-emphasis font-normal">{t('FIRST_NAME')}</FormLabel>
                <FormControl>
                  <Input type="text" placeholder={t('ENTER_YOUR_FIRST_NAME')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-high-emphasis font-normal">{t('LAST_NAME')}</FormLabel>
                <FormControl>
                  <Input type="text" placeholder={t('ENTER_YOUR_LAST_NAME')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-high-emphasis font-normal">{t('EMAIL')}</FormLabel>
              <FormControl>
                <Input type="email" placeholder={t('ENTER_YOUR_EMAIL')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Captcha
          type="reCaptcha"
          siteKey={configurationSettings.googleCaptchaSiteKey}
          theme="light"
          onVerify={handleCaptchaVerify}
          onExpired={handleCaptchaExpired}
          setResetFn={setResetCaptcha}
          size="normal"
        />

        <div className="flex justify-between items-center">
          <FormCheckbox
            control={form.control}
            name="terms"
            label={
              <>
                I agree to the{' '}
                <span className="text-primary underline hover:text-primary-600">
                  <a href="#">Terms of Service</a>
                </span>{' '}
                and acknowledge that I have read the{' '}
                <span className="text-primary underline hover:text-primary-600">
                  <a href="#">Privacy policy.</a>
                </span>
              </>
            }
            labelClassName="text-medium-emphasis font-normal leading-5"
          />
        </div>

        <div className="flex gap-10">
          <Button
            className="flex-1 font-extrabold"
            size="lg"
            type="submit"
            disabled={isSubmitting || !captchaToken || !isValid}
            loading={isPending}
          >
            {t('SIGN_UP')}
          </Button>
        </div>
      </form>
    </Form>
  );
};
